import { StandardButton } from '@/components/buttons/standard';
import { CustomContentSection } from '@/components/custom-content-section';
import { useLayoutContext } from '@/contexts';
import { PrimaryLayout } from '@/layouts/primary';
import { Flex } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { AccountDetailsForm, SignupFormValues } from './form';
import { useUpdateUserDetailsMutation } from './graphql.generated';

export const SignupDetailsPage = () => {
	const navigate = useNavigate();
	const { isDesktop } = useLayoutContext();
	const [updateUserDetails, { loading: updatingUser }] = useUpdateUserDetailsMutation();
	const ref = useRef<FormikProps<SignupFormValues>>(null);
	const saveForm = useCallback(() => {
		ref.current?.submitForm();
	}, []);
	const saveAccountButton = useMemo(
		() => (
			<StandardButton loading={updatingUser} onClick={saveForm}>
				Save Account
			</StandardButton>
		),
		[saveForm, updatingUser]
	);

	const handleSubmit = useCallback(
		async (values: SignupFormValues) => {
			try {
				await updateUserDetails({
					variables: {
						input: {
							formattedAddress: values.formattedAddress,
							addressLine1: values.addressLine1,
							addressLine2: values.addressLine2,
							city: values.city,
							council: values.council,
							dwellingType: values.dwellingType,
							gender: values.gender,
							householdSize: values.householdSize,
							phoneNumber: values.phoneNumber || null,
							postcode: values.postcode,
							state: values.state,
							lat: values.lat,
							lng: values.lng,
							isManualAddress: values.isManualAddress,
							placeId: values.placeId,
						},
					},
				});

				navigate({ to: '/about-subscriptions' });
			} catch (error) {
				// TODO: need some error handling to give the user feedback
				console.error(error);
			}
		},
		[updateUserDetails, navigate]
	);

	return (
		<PrimaryLayout header={{ title: 'Account Details' }} footer={{ children: [saveAccountButton] }}>
			<Flex direction="column" justify="space-between" h="100%" w="100%">
				<Flex direction="column" align="center" justify="flex-start" w="100%">
					<CustomContentSection
						location={ContentSectionLocation.SignUpDetails}
						placement={ContentSectionPlacement.Top}
					/>
					<AccountDetailsForm onSubmit={handleSubmit} formRef={ref} />
					<CustomContentSection
						location={ContentSectionLocation.SignUpDetails}
						placement={ContentSectionPlacement.Middle}
					/>
					<CustomContentSection
						location={ContentSectionLocation.SignUpDetails}
						placement={ContentSectionPlacement.Bottom}
					/>
				</Flex>
			</Flex>
			{isDesktop && (
				<Flex
					direction="row"
					align="center"
					justify="center"
					pos="fixed"
					w="100%"
					bg="white"
					bottom={0}
					py="sm"
					bd="1px solid #e0e0e0"
				>
					<Flex w={200}>{saveAccountButton}</Flex>
				</Flex>
			)}
		</PrimaryLayout>
	);
};
